import { GetItemsProps, useApiService } from "medere-ui-framework";
import { AccountTypeDTO, Bank, BankAccount, BankAccountDTO } from "../../interface/interface";
import { ComboDataDTO } from "../../dto/ComboDataDTO";

interface BankAccountWithPaymentMethodBanks extends Omit<BankAccountDTO, "paymentmethodbanks"> {
  paymentmethodbanks: number[]
}

export function useBankService() {
  const { api, getMethod, postMethod, tableMethod } = useApiService();
  const baseEndpoint = "/api/secured/banks";

  function getAllBanks(offSet: number, pageSize: number, params: any): Promise<Bank[]> {
    return tableMethod<Bank[]>({
      basePath: baseEndpoint,
      endpoint: "/getAllBanks",
      bodyTable: {
        offSet,
        pageSize,
        sortingFieldInfoList: params.multiSortMeta,
        filters: params.filterDTO
      }
    });
  }

  function getAllBanksForCombo({ offSet, pageSize, filterValue }: GetItemsProps): Promise<ComboDataDTO[]> {
    return getMethod<ComboDataDTO[]>({
      basePath: baseEndpoint,
      endpoint: "/getAllBanksForCombo",
      params: { offSet, pageSize, inputtext: filterValue }
    });
  }

  function getBankFull(bankId: number) {
    return getMethod<Bank>({
      basePath: baseEndpoint,
      endpoint: "/getBankFull",
      params: { bankId }
    });
  }

  function createBank(bank: Bank): Promise<number> {
    return postMethod<number>({
      basePath: baseEndpoint,
      endpoint: "/createBank",
      body: bank
    });
  }

  function updateBank(bank: Bank) {
    return postMethod({
      basePath: baseEndpoint,
      endpoint: "/updateBank",
      body: bank
    });
  }

  function deleteBank(bankId: number) {
    return api.post(`${baseEndpoint}/deleteBank`, null, { params: { bankId } });
  }

  function getAllAccountTypes({ offSet, pageSize, filterValue }: GetItemsProps): Promise<AccountTypeDTO[]> {
    return getMethod<AccountTypeDTO[]>({
      basePath: baseEndpoint,
      endpoint: "/getAllAccountTypes",
      params: { offSet, pageSize, inputtext: filterValue }
    });
  }

  function getAllCurrencies({ offSet, pageSize, filterValue }: GetItemsProps): Promise<ComboDataDTO[]> {
    return getMethod<ComboDataDTO[]>({
      basePath: baseEndpoint,
      endpoint: "/getAllCurrencies",
      params: { offSet, pageSize, inputtext: filterValue }
    });
  }

  function getAllBankAccountsByMedereEntity(offSet: number, pageSize: number, params: any): Promise<BankAccountDTO[]> {
    return tableMethod<BankAccountDTO[]>({
      basePath: baseEndpoint,
      endpoint: "/getAllBankAccountsByMedereEntity",
      bodyTable: {
        offSet,
        pageSize,
        sortingFieldInfoList: params.multiSortMeta,
        filters: params.filterDTO
      },
      params: { medereEntityId: params.medereentity }
    });
  }

  function getAllBankAccountsComboByMedereEntity({ filterValue, offSet, pageSize, params }: GetItemsProps): Promise<BankAccountDTO[]> {
    return getMethod<BankAccountDTO[]>({
      basePath: baseEndpoint,
      endpoint: "/getAllBankAccountsComboByMedereEntity",
      params: {
        inputText: filterValue,
        offSet,
        pageSize,
        medereEntityId: params.medereentity,
        onlyWithPaymentMethods: params?.onlyWithPaymentMethods
      }
    });
  }

  function getPaymentMethodBanks(bankAccountId: number): Promise<any> {
    return getMethod<any>({
      basePath: baseEndpoint,
      endpoint: "/getPaymentMethodBanks",
      params: { bankAccountId }
    });
  }

  function getPaymentMethodsByBankAccount({ filterValue, offSet, pageSize, params }: GetItemsProps): Promise<any[]> {
    return getMethod<any[]>({
      basePath: baseEndpoint,
      endpoint: "/getPaymentMethodsByBankAccount",
      params: {
        inputText: filterValue,
        offSet,
        pageSize,
        bankAccountId: params.bankAccountId
      }
    });
  }

  function saveBankAccount(bankAccount: BankAccountWithPaymentMethodBanks): Promise<any> {
    return postMethod({
      basePath: baseEndpoint,
      endpoint: "/saveBankAccount",
      body: bankAccount
    });
  }

  function deleteBankAccount(bankAccountId: number): Promise<any> {
    return postMethod({
      basePath: baseEndpoint,
      endpoint: "/deleteBankAccount",
      body: null,
      params: { bankAccountId }
    });
  }

  function updateBankAccount(bankAccount: BankAccountWithPaymentMethodBanks): Promise<any> {
    return postMethod({
      basePath: baseEndpoint,
      endpoint: "/updateBankAccount",
      body: bankAccount
    });
  }

  function getBankAccount(bankAccountId: number): Promise<BankAccount> {
    return getMethod<BankAccount>({
      basePath: baseEndpoint,
      endpoint: "/getBankAccount",
      params: { bankAccountId }
    });
  }

  function isCBURepeated(bank: number, cbu: string) {
    return api.get(`${baseEndpoint}/isCBURepeated`, {
      params: { bank, cbu }
    }).then(response => ({ statusCode: response.status }));
  }

  function bankHasBankAccounts(bank: number) {
    return api.get(`${baseEndpoint}/bankHasBankAccounts`, {
      params: { bank }
    }).then(response => ({ statusCode: response.status }));
  }

  return {
    getAllBanks,
    getAllBanksForCombo,
    getBankFull,
    createBank,
    updateBank,
    deleteBank,
    getAllAccountTypes,
    getAllCurrencies,
    getAllBankAccountsByMedereEntity,
    getAllBankAccountsComboByMedereEntity,
    getPaymentMethodBanks,
    getPaymentMethodsByBankAccount,
    saveBankAccount,
    deleteBankAccount,
    updateBankAccount,
    getBankAccount,
    isCBURepeated,
    bankHasBankAccounts
  };
}
